import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import {
  listEntities, getLatestTelemetry, EntityRecord, EntityType,
} from '../services/entitiesService';
import { MavlinkTelemetryAdapter } from '../services/telemetry/MavlinkTelemetryAdapter';
import { NormalizedTelemetry } from '../services/telemetry/TelemetryProvider';

/**
 * TelemetryLayer
 *
 * Renders drones / vehicles / ground-control-stations on the Mapbox map
 * driven by the backend's /api/entities + /ws/mavlink stack.  Owns a
 * singleton MavlinkTelemetryAdapter that subscribes to live frames
 * (telemetry pushed by connected PX4/MAVLink devices) and a poller that
 * refreshes the entity list + latest telemetry for anything the WS
 * hasn't moved recently.
 *
 * Presentation rules:
 *   - Drones / flying → blue triangle, icon "✈"
 *   - Vehicles        → orange pin,     icon "🚗"
 *   - GCUs            → grey square,    icon "🎮"
 *   - Other entities  → purple dot,     icon "•"
 *
 * Privacy:
 *   - Entity markers show the approximate region (0.1°-quantised) in
 *     the popup by default with an eye-toggle to reveal precise lat/lng,
 *     matching the GamePanel + live broadcast HUD convention.
 *
 * The component mounts into the same Mapbox instance as the rest of the
 * app.  It avoids using `addLayer` — with many short-lived sources that
 * gets noisy — and instead manages native Marker objects per entity.
 */

export interface TelemetryLayerProps {
  map: mapboxgl.Map | null;
  /** Poll interval for the /api/entities list (ms).  Default 30s. */
  refreshMs?: number;
  /** If false, the component is inert — useful behind a feature flag. */
  enabled?: boolean;
}

type EntityState = {
  entity: EntityRecord;
  telemetry: NormalizedTelemetry | null;
  marker: mapboxgl.Marker | null;
  popup: mapboxgl.Popup | null;
  revealExact: boolean;
};

const TRACKED_TYPES: EntityType[] = ['drone', 'vehicle', 'ground_control_station'];

function iconFor(type: EntityType): { icon: string; bg: string; fg: string } {
  switch (type) {
    case 'drone':
      return { icon: '✈', bg: '#2563eb', fg: '#fff' };
    case 'vehicle':
      return { icon: '🚗', bg: '#ea580c', fg: '#fff' };
    case 'ground_control_station':
      return { icon: '🎮', bg: '#334155', fg: '#fff' };
    default:
      return { icon: '•', bg: '#7c3aed', fg: '#fff' };
  }
}

function approxRegionLabel(lat: number, lon: number): string {
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return '—';
  const latStr = `${Math.abs(lat).toFixed(1)}°${lat >= 0 ? 'N' : 'S'}`;
  const lonStr = `${Math.abs(lon).toFixed(1)}°${lon >= 0 ? 'E' : 'W'}`;
  return `${latStr} · ${lonStr}`;
}

function formatMetric(value: number | null | undefined, unit = ''): string {
  if (value == null || !Number.isFinite(value as number)) return '—';
  return `${(value as number).toFixed(1)}${unit}`;
}

function renderPopupHtml(s: EntityState): string {
  const { entity, telemetry, revealExact } = s;
  const tel = telemetry;
  const pos = tel?.position || null;
  const { icon } = iconFor(entity.entityType);
  const trust = tel?.trustLevel || 'unverified';
  const trustColor = trust === 'signed' ? '#059669' : trust === 'attested' ? '#0d9488' : '#94a3b8';
  const coordsBlock = pos
    ? (revealExact
        ? `<div style="font-family:ui-monospace,Menlo,monospace;font-size:12px;color:#0f172a">
             ${pos.lat.toFixed(5)}, ${pos.lon.toFixed(5)}
             ${pos.alt_msl != null ? `<span style="color:#64748b"> · ${pos.alt_msl.toFixed(1)}m MSL</span>` : ''}
           </div>`
        : `<div style="font-size:12px;color:#334155">
             Near <strong>${approxRegionLabel(pos.lat, pos.lon)}</strong>
             <span style="color:#94a3b8"> (precise hidden)</span>
           </div>`)
    : `<div style="font-size:12px;color:#94a3b8">No position yet</div>`;

  const metrics: string[] = [];
  if (tel?.velocity?.groundSpeed != null) metrics.push(`<span>GS ${formatMetric(tel.velocity.groundSpeed, ' m/s')}</span>`);
  if (tel?.heading?.courseDeg != null)    metrics.push(`<span>Hdg ${formatMetric(tel.heading.courseDeg, '°')}</span>`);
  if (tel?.battery?.remainingPct != null) metrics.push(`<span>Batt ${formatMetric(tel.battery.remainingPct, '%')}</span>`);
  if (tel?.health?.satellites != null)    metrics.push(`<span>SAT ${Math.round(tel.health.satellites)}</span>`);
  if (tel?.flightMode)                    metrics.push(`<span>${tel.flightMode}</span>`);

  return `
    <div style="min-width:220px;font-family:system-ui,-apple-system,Segoe UI,sans-serif">
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px">
        <span style="font-size:18px">${icon}</span>
        <strong style="font-size:13px;flex:1">${escapeHtml(entity.displayName || entity.id)}</strong>
        <span style="font-size:10px;padding:2px 6px;border-radius:999px;background:${trustColor};color:#fff;text-transform:uppercase;letter-spacing:0.5px">
          ${trust}
        </span>
      </div>
      <div style="font-size:11px;color:#64748b;margin-bottom:6px">
        ${entity.entityType.replace(/_/g, ' ')} · sysid ${entity.systemId}${entity.componentId ? `.${entity.componentId}` : ''}
      </div>
      ${coordsBlock}
      ${metrics.length ? `<div style="margin-top:6px;display:flex;flex-wrap:wrap;gap:6px;font-size:11px;color:#334155">${metrics.join(' · ')}</div>` : ''}
      ${renderFaaComplianceBlock(entity)}
      <button data-telemetry-toggle="${entity.id}" style="margin-top:8px;padding:4px 10px;border:1px solid #cbd5e1;background:${revealExact ? '#0ea5e9' : '#fff'};color:${revealExact ? '#fff' : '#334155'};border-radius:999px;font-size:11px;cursor:pointer">
        ${revealExact ? '🙈 Hide exact location' : '👁 Reveal exact location'}
      </button>
    </div>
  `;
}

/**
 * Slice 7 — operator-attested FAA Remote ID + registration block.
 * Only shown for drones / vehicles with a faaCompliance record.
 * The registration number, Remote ID serial, and broadcast type
 * are publicly displayable per 14 CFR Part 89; the verification
 * source is rendered as a small badge so observers can tell at a
 * glance whether the data is self-attested or third-party verified
 * (the latter requires the future GeoLink FAA registry lookup).
 */
function renderFaaComplianceBlock(entity: EntityRecord): string {
  const c = entity.faaCompliance;
  if (!c) return '';
  const vs = c.verificationSource || 'self-attested';
  const badgeLabel =
    vs === 'self-attested'
      ? 'SELF-ATTESTED'
      : vs === 'geolink-verified'
        ? 'GEOLINK REGISTRY MATCH'
        : String(vs).toUpperCase();
  const verifiedBadge =
    vs === 'self-attested'
      ? `<span style="font-size:9px;padding:1px 5px;border-radius:999px;background:#fde68a;color:#78350f;margin-left:4px">${badgeLabel}</span>`
      : `<span style="font-size:9px;padding:1px 5px;border-radius:999px;background:#bbf7d0;color:#14532d;margin-left:4px">${badgeLabel}</span>`;
  const typeLabel: Record<string, string> = {
    standard:   'Standard Remote ID',
    module:     'Remote ID Module',
    fria_only:  'FRIA-only',
    none:       'No Remote ID',
  };
  return `
    <div style="margin-top:8px;padding:6px 8px;background:#f8fafc;border-left:2px solid #2563eb;border-radius:3px">
      <div style="font-size:10px;font-weight:600;color:#475569;text-transform:uppercase;letter-spacing:0.5px;display:flex;align-items:center">
        FAA Remote ID${verifiedBadge}
      </div>
      <div style="font-size:11px;color:#0f172a;font-family:ui-monospace,Menlo,monospace;margin-top:3px">
        ${escapeHtml(c.registrationNumber)}
      </div>
      ${c.remoteIdSerial ? `
        <div style="font-size:10px;color:#475569;font-family:ui-monospace,Menlo,monospace">
          serial: ${escapeHtml(c.remoteIdSerial)}
        </div>` : ''}
      <div style="font-size:10px;color:#64748b;margin-top:2px">
        ${typeLabel[c.remoteIdType] || c.remoteIdType}${c.remoteIdMethod !== 'none' ? ' · ' + c.remoteIdMethod : ''}
      </div>
    </div>
  `;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c] || c));
}

export const TelemetryLayer: React.FC<TelemetryLayerProps> = ({
  map, refreshMs = 30_000, enabled = true,
}) => {
  const adapterRef = useRef<MavlinkTelemetryAdapter | null>(null);
  const entitiesRef = useRef<Map<string, EntityState>>(new Map());
  const revealByIdRef = useRef<Map<string, boolean>>(new Map());
  // Refs hold the latest map instance + imperative helpers so our effects
  // can keep stable dep lists without stale closures — the adapter's WS
  // must not tear down every time `map` becomes non-null, and React's
  // exhaustive-deps rule would otherwise force us to include these
  // changing-but-logically-stable callbacks.
  const mapRef = useRef<mapboxgl.Map | null>(map);
  useEffect(() => { mapRef.current = map; }, [map]);
  const [, forceRerender] = useState(0);

  // Tick used only to rebuild popup HTML when the reveal-toggle flips.
  // The markers themselves are managed imperatively against mapbox.
  const bump = () => forceRerender((n) => n + 1);

  const updateMarker = (state: EntityState) => {
    const currentMap = mapRef.current;
    if (!currentMap) return;
    const pos = state.telemetry?.position;
    if (!pos) {
      if (state.marker) { try { state.marker.remove(); } catch { /* ignore */ } state.marker = null; }
      return;
    }
    const { icon, bg, fg } = iconFor(state.entity.entityType);

    if (!state.marker) {
      const el = document.createElement('div');
      el.style.cssText = `
        display: flex; align-items: center; justify-content: center;
        width: 30px; height: 30px; border-radius: 50%;
        background: ${bg}; color: ${fg};
        box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        font-size: 16px; cursor: pointer; user-select: none;
        border: 2px solid rgba(255,255,255,0.85);
      `;
      el.textContent = icon;
      el.title = state.entity.displayName || state.entity.id;

      const popup = new mapboxgl.Popup({ closeButton: true, maxWidth: '260px' });
      popup.setHTML(renderPopupHtml(state));
      popup.on('open', () => {
        const node = popup.getElement();
        if (!node) return;
        const btn = node.querySelector<HTMLElement>(`[data-telemetry-toggle="${state.entity.id}"]`);
        if (btn) {
          btn.addEventListener('click', (ev) => {
            ev.stopPropagation();
            state.revealExact = !state.revealExact;
            revealByIdRef.current.set(state.entity.id, state.revealExact);
            popup.setHTML(renderPopupHtml(state));
            bump();
          });
        }
      });

      const marker = new mapboxgl.Marker({ element: el })
        .setLngLat([pos.lon, pos.lat])
        .setPopup(popup)
        .addTo(currentMap);
      state.marker = marker;
      state.popup = popup;
    } else {
      state.marker.setLngLat([pos.lon, pos.lat]);
    }
  };

  const reconcileEntities = async (records: EntityRecord[]) => {
    const seen = new Set<string>();
    for (const entity of records) {
      seen.add(entity.id);
      let state = entitiesRef.current.get(entity.id);
      if (!state) {
        state = {
          entity,
          telemetry: null,
          marker: null,
          popup: null,
          revealExact: revealByIdRef.current.get(entity.id) ?? false,
        };
        entitiesRef.current.set(entity.id, state);
      } else {
        state.entity = entity;
      }
      if (!state.telemetry?.position) {
        try {
          const latest = await getLatestTelemetry(entity.id);
          if (latest) state.telemetry = latest as NormalizedTelemetry;
        } catch { /* ignore */ }
      }
      updateMarker(state);
    }

    const removedIds: string[] = [];
    entitiesRef.current.forEach((state, id) => {
      if (!seen.has(id)) {
        try { state.marker?.remove(); } catch { /* ignore */ }
        try { state.popup?.remove();  } catch { /* ignore */ }
        removedIds.push(id);
      }
    });
    removedIds.forEach((id) => entitiesRef.current.delete(id));
  };

  // Stable refs for the helpers so our long-lived effects can call the
  // latest versions without re-subscribing.
  const updateMarkerRef = useRef(updateMarker);
  const reconcileRef    = useRef(reconcileEntities);
  useEffect(() => { updateMarkerRef.current = updateMarker; });
  useEffect(() => { reconcileRef.current    = reconcileEntities; });

  // ── Adapter lifecycle ──
  useEffect(() => {
    if (!enabled) return;
    const adapter = new MavlinkTelemetryAdapter({});
    adapterRef.current = adapter;
    const offUpdate = adapter.onUpdate((rec) => {
      if (!rec.entityId) return;
      const st = entitiesRef.current.get(rec.entityId);
      if (!st) return;
      st.telemetry = rec;
      updateMarkerRef.current(st);
      if (st.popup && st.popup.isOpen()) {
        st.popup.setHTML(renderPopupHtml(st));
      }
    });
    const offError = adapter.onError((err) => {
      // Silent: the MAVLink socket is optional; the REST poll path keeps
      // the UI functional even when no gateway is active.
    });
    void adapter.start();
    return () => {
      offUpdate(); offError();
      adapter.stop();
      adapterRef.current = null;
    };
  }, [enabled]);

  // ── Entity polling ──
  useEffect(() => {
    if (!enabled || !map) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const lists = await Promise.all(
          TRACKED_TYPES.map((t) => listEntities({ type: t }).catch(() => [] as EntityRecord[])),
        );
        if (cancelled) return;
        await reconcileRef.current(lists.flat());
      } catch {
        // Poll is best-effort; swallow so one bad response doesn't break
        // the layer.  Real errors surface in the browser console via fetch.
      }
    };
    tick();
    const timer = window.setInterval(tick, refreshMs);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [map, enabled, refreshMs]);

  // ── Cleanup all markers on unmount ──
  useEffect(() => () => {
    entitiesRef.current.forEach((s) => {
      try { s.marker?.remove(); } catch { /* ignore */ }
      try { s.popup?.remove(); } catch { /* ignore */ }
    });
    entitiesRef.current.clear();
  }, []);

  return null;
};

export default TelemetryLayer;
